import styled from '@emotion/styled';
import React from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { AutoSaveIndicator } from '../../../../../components/form/AutoSaveIndicator';
import { FormGroup } from '../../../../../components/form/FormGroup';
import { useToasts } from '../../../../../context/Toast';
import { Theme } from '../../../../../theme';
import { FILE_SIZE_LIMIT, toBase64 } from '../../../../../utils/file';
import {
  DocumentVisiblity,
  WorkflowItemFileFormat,
} from '../../../__generated__/graphql';
import { mapWorkflowItemFileFormatToFileSpecifier } from './mappings';
import { FileOnServer } from './types';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import { fileSizeFormatter } from '../../../../../utils/file';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import LightSectionContainer from './LightSectionContainer';
import { FileInput } from '../../../__generated__/graphql';

type Props = {
  fileFormat: WorkflowItemFileFormat;
  file: FileOnServer | null;
  onSelectFile: (fileInput: FileInput) => void;
  onDeleteFile: (fileId: string) => void;
  readonly: boolean;
  saving: boolean;
  saved: boolean;
  visibility: DocumentVisiblity;
  onVisibilityChange: (visibility: DocumentVisiblity) => void;
  visibilityOptions?: DocumentVisiblity[];
};

export const WorkflowItemFileField: React.FC<
  React.PropsWithChildren<Props>
> = ({
  file,
  fileFormat,
  saving,
  saved,
  readonly,
  onSelectFile,
  onDeleteFile,
  visibility,
  onVisibilityChange,
  visibilityOptions,
}) => {
  const { t } = useTranslation();
  const tSidebar = useNamespacedTranslation(
    'performanceReview.workflowsItems.sidebar',
  );
  const { addError } = useToasts();

  const visibilityOptionsList =
    visibilityOptions ??
    (Object.values(DocumentVisiblity) as DocumentVisiblity[]);

  const translationsOptionsMap = {
    [DocumentVisiblity.Hr]: 'hr',
    [DocumentVisiblity.HrEmployee]: 'hrEmployee',
    [DocumentVisiblity.HrManager]: 'hrManager',
    [DocumentVisiblity.HrManagerEmployee]: 'hrManagerEmployee',
  };
  const translateVisibilityOption = (option: DocumentVisiblity) =>
    t(
      `documents2.personal.upload.visibility.${translationsOptionsMap[option]}`,
    );

  const visibilityLabel = t(
    'documents2.personal.upload.selectVisibilityPlaceholder',
  );

  const handleVisibilitySelect = ({
    target,
  }: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = target;
    if (isDocumentVisibility(value)) {
      onVisibilityChange(value);
    }
  };

  const fileInputAcceptType =
    mapWorkflowItemFileFormatToFileSpecifier(fileFormat);

  const sizeLimit = fileSizeFormatter(FILE_SIZE_LIMIT);

  const selectFileLabel = fileInputAcceptType
    ? tSidebar('selectFileWithFormatAndSize', {
        fileFormat: t(
          `performanceReview.workflowsItems.fileFormats.${fileFormat}`,
        ),
        size: sizeLimit,
      })
    : tSidebar('selectFileWithSize', {
        size: sizeLimit,
      });

  const handleFileSelect = async ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    if (saving) {
      target.value = '';
      return;
    }
    if (target.validity.valid && target.files) {
      const [file] = Array.from(target.files);

      if (file.size > FILE_SIZE_LIMIT) {
        addError(t('documents.upload.sizeLimitError'));
        target.value = '';
      } else {
        getFileInputToUpload(file).then(onSelectFile);
      }
    }
  };

  const handleDelete = () => {
    if (saving || !file) {
      return;
    }
    onDeleteFile(file.id);
  };

  if (readonly) {
    return (
      <FormGroup>
        <Form.Label>{tSidebar('file')}:</Form.Label>
        <div className="d-flex flex-column">
          <span>{file ? file.name : tSidebar('notUploadedYet')}</span>
          {file ? (
            <span className="mt-2">
              {visibilityLabel}: {translateVisibilityOption(visibility)}
            </span>
          ) : null}
        </div>
      </FormGroup>
    );
  }

  return (
    <LightSectionContainer>
      <h4 className="mb-4">{tSidebar('uploadFileToComplete')}</h4>
      <FormGroup>
        <Form.Label>
          {selectFileLabel}
          <div className="d-inline-block ms-2">
            <AutoSaveIndicator loading={saving} completed={saved} />
          </div>
        </Form.Label>
        {file === null ? (
          <Form.Control
            id="file"
            type="file"
            accept={fileInputAcceptType}
            onChange={handleFileSelect}
          />
        ) : (
          <div className="d-flex flex-row">
            <span>{file.name}</span>
            <DeleteButton onClick={handleDelete}>
              <FlairIcon icon="trash-outline" />
            </DeleteButton>
          </div>
        )}
      </FormGroup>
      <FormGroup>
        <Form.Label>{visibilityLabel}</Form.Label>
        <Form.Select
          value={visibility}
          onChange={handleVisibilitySelect}
          disabled={saving}>
          {visibilityOptionsList.map((option) => (
            <option key={option} value={option}>
              {translateVisibilityOption(option)}
            </option>
          ))}
        </Form.Select>
      </FormGroup>
      <small>{tSidebar('informationAutomaticallySaved')}</small>
    </LightSectionContainer>
  );
};

const getFileInputToUpload = (file: File): Promise<FileInput> => {
  return toBase64(file).then((fileContentBase64) => ({
    fileName: file.name,
    fileContentBase64,
  }));
};

const DeleteButton = styled.div({
  cursor: 'pointer',
  color: Theme.color.gray3,
  transition: 'color 0.15s',
  marginLeft: '0.75rem',
  ':hover': {
    color: Theme.color.gray2,
  },
});

const isDocumentVisibility = (value: string): value is DocumentVisiblity => {
  return Object.values(DocumentVisiblity).includes(value as DocumentVisiblity);
};
